import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { FiPlus, FiChevronLeft, FiChevronRight, FiEye, FiEdit2, FiTrash2, FiClock, FiUsers, FiSearch, FiImage } from 'react-icons/fi';
import ViewShopModal from '../components/ViewShopModal';
import ShopViewMediaModal from '../components/ShopViewMediaModal';
import EditShopModal from '../components/EditShopModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import CreateShopForm from '../components/CreateShopForm';
import ErrorBoundary from '../components/ErrorBoundary';
import Pagination from '../components/Pagination';

const ShopContent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [selectedShop, setSelectedShop] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalShops, setTotalShops] = useState(0);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const [visibilityFilter, setVisibilityFilter] = useState('all'); // 'all' | 'visible' | 'hidden'
    const [statusFilter, setStatusFilter] = useState('');
    const [isUpdatedFilter, setIsUpdatedFilter] = useState('');

    const fetchShops = async (page, search = '', visibility = 'all', status = '', isUpdated = '') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                page,
                limit: 20,
                populate: 'godMember',
                search: search || '',
                visibility: visibility || 'all',
            });
            if (status) params.set('status', status);
            if (isUpdated) params.set('isUpdated', isUpdated);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/shops?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setShops(response.data.data);
                setTotalPages(response.data.totalPages);
                setTotalShops(response.data.total);
            }
        } catch (err) {
            setError(`Failed to fetch shops: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchShops(currentPage, searchTerm, visibilityFilter, statusFilter, isUpdatedFilter);
        }, 300); // 300ms delay

        return () => clearTimeout(delayDebounceFn);
    }, [currentPage, searchTerm, visibilityFilter, statusFilter, isUpdatedFilter]);

    // Open create form when navigated with ?open=create (e.g. from Layout action bar)
    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreateForm(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleCreateSuccess = () => {
        setShowCreateForm(false);
        fetchShops(currentPage, searchTerm, visibilityFilter, statusFilter, isUpdatedFilter);
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        setSelectedShop(null);
        fetchShops(currentPage, searchTerm, visibilityFilter, statusFilter, isUpdatedFilter);
    };

    const handleView = async (shop) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/shops/${shop._id}?populate=godMember`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setSelectedShop(response.data.data);
                setShowViewModal(true);
            }
        } catch (err) {
            setError(`Failed to fetch shop details: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleEdit = (shop) => {
        setSelectedShop(shop);
        setShowEditModal(true);
    };

    const handleDelete = (shop) => {
        setSelectedShop(shop);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            setDeleteLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/shops/${selectedShop._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedShop(null);
            fetchShops(currentPage, searchTerm, visibilityFilter, statusFilter, isUpdatedFilter);
        } catch (err) {
            setError(`Failed to delete shop: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        // Time is already in HH:mm format, just return it
        return timeString;
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800';
            case 'inactive':
                return 'bg-gray-100 text-gray-800';
            case 'closed':
                return 'bg-red-100 text-red-800';
            case 'premium':
                return 'bg-purple-100 text-purple-800';
            case 'feature':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <Layout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Shops</h1>
                        <p className="text-gray-600">Total: {totalShops} shops</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Add New Shop
                    </button>
                </div>

                {/* Search Bar & Filters */}
                <div className="mb-6 flex flex-wrap items-center gap-4">
                    <div className="relative flex-1 min-w-[200px]">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiSearch className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by shop name, address, area, region, contact or website..."
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="closed">Closed</option>
                        <option value="premium">Premium</option>
                        <option value="feature">Feature</option>
                    </select>
                    <select
                        value={isUpdatedFilter}
                        onChange={(e) => setIsUpdatedFilter(e.target.value)}
                        className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                        <option value="">All (updated / not)</option>
                        <option value="true">Updated</option>
                        <option value="false">Not updated</option>
                    </select>
                    <select
                        value={visibilityFilter}
                        onChange={(e) => setVisibilityFilter(e.target.value)}
                        className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        title="Filter by hidden status"
                    >
                        <option value="all">All shops</option>
                        <option value="visible">Visible only</option>
                        <option value="hidden">Hidden only</option>
                    </select>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Shop Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Address
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    isUpdated
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Region/Area
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Operating Hours
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Members
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : shops.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center">
                                        No shops found
                                    </td>
                                </tr>
                            ) : (
                                shops.map((shop) => (
                                    <tr key={shop._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="font-medium">
                                                {shop.shopName}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm">{shop.address}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-lg" title={shop.isUpdated ? 'Updated' : 'Not updated'}>
                                                {shop.isUpdated ? '✅' : '❌'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>{shop.region}</div>
                                            <div className="text-sm text-gray-500">{shop.area}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <FiClock className="mr-2 text-gray-400" />
                                                <span className="text-sm">
                                                    {formatTime(shop.openHour)} - {formatTime(shop.closeHour)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-start">
                                                <FiUsers className="mr-2 text-gray-400 mt-1" />
                                                <div className="text-xs text-gray-600">
                                                    {shop.godMember && shop.godMember.length > 0 ? (
                                                        shop.godMember.map((member, index) => (
                                                            <div key={member._id}>
                                                                {member.fullName}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div>No members</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shop.status)}`}>
                                                {shop.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => { setSelectedShop(shop); setShowMediaModal(true); }}
                                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full inline-flex items-center"
                                                title="Media"
                                            >
                                                <FiImage className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleView(shop)}
                                                className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                title="View Details"
                                            >
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(shop)}
                                                className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                title="Edit Shop"
                                            >
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(shop)}
                                                className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                title="Delete Shop"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />

                {/* Create Form Modal */}
                {showCreateForm && (
                    <CreateShopForm
                        onSuccess={handleCreateSuccess}
                        onCancel={() => setShowCreateForm(false)}
                    />
                )}

                {/* Media Modal */}
                {showMediaModal && selectedShop && (
                    <ShopViewMediaModal
                        shop={selectedShop}
                        onClose={() => setShowMediaModal(false)}
                        onMediaUpdate={() => fetchShops(currentPage, searchTerm, visibilityFilter, statusFilter, isUpdatedFilter)}
                    />
                )}

                {/* View Modal */}
                {showViewModal && selectedShop && (
                    <ViewShopModal
                        shop={selectedShop}
                        onClose={() => {
                            setShowViewModal(false);
                            setSelectedShop(null);
                        }}
                    />
                )}

                {/* Edit Modal */}
                {showEditModal && selectedShop && (
                    <EditShopModal
                        shop={selectedShop}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedShop(null);
                        }}
                    />
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && selectedShop && (
                    <DeleteConfirmationModal
                        title="Delete Shop"
                        message="Are you sure you want to delete this shop? This action cannot be undone."
                        itemName={selectedShop.shopName}
                        isLoading={deleteLoading}
                        onConfirm={confirmDelete}
                        onCancel={() => {
                            setShowDeleteConfirm(false);
                            setSelectedShop(null);
                        }}
                    />
                )}
            </div>
        </Layout>
    );
};

const ShopPage = () => {
    return (
        <ErrorBoundary
            fallbackTitle="Shop Management Error"
            fallbackDescription="An error occurred while managing shops. Please try again or contact support if the problem persists."
        >
            <ShopContent />
        </ErrorBoundary>
    );
};

export default ShopPage; 